import { useCallback, useEffect } from "react";

import { CellStatus } from "../primitives";

import { ReportCell, ColumnType } from "@/types/evaluate";
import { SelectedCell } from "@/components/ui/TableV2/grid-store";
import { forceJSONString } from "@/utils/strings";
import { isModifierKeyPressed } from "@/utils/shortcuts";

const MAX_CHARS_TO_RENDER = 1500;

export const useCellDisplayData = (
  cell?: ReportCell,
  column?: { column_type: ColumnType },
  isFullscreen = false,
  selectedCell: SelectedCell | null = null,
) => {
  const isCellSelected = selectedCell?.id === cell?.id;

  let displayVal = "";
  let truncate = false;

  switch (cell?.display_value?.type) {
    case "string":
      displayVal = cell?.display_value.value;
      truncate = displayVal?.length > MAX_CHARS_TO_RENDER && !isFullscreen && !isCellSelected;
      break;
    case "number":
      displayVal = String(cell?.display_value.value);
      truncate = false;
      break;
    case "object":
      displayVal = forceJSONString(cell?.display_value.value);
      truncate =
        displayVal?.length > MAX_CHARS_TO_RENDER &&
        !isFullscreen &&
        !isCellSelected &&
        column?.column_type !== ColumnType.LLM_ASSERTION;
      break;
    case "function":
    case "tool_calls":
      displayVal = JSON.stringify(cell?.display_value.value, null, 2);
      truncate = false;
      break;
    case "boolean":
      displayVal = cell?.display_value.value ? "true" : "false";
      truncate = false;
      break;
    case "unknown":
      displayVal = "—";
      truncate = false;
      break;
    default:
      if (cell?.display_value) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { value } = cell.display_value as any;
        const valueType = typeof value;

        if (valueType === "object" && value !== null) {
          displayVal = JSON.stringify(value, null, 2);
          truncate = displayVal?.length > MAX_CHARS_TO_RENDER && !isFullscreen && !isCellSelected;
        } else if (value !== undefined && value !== null) {
          displayVal = String(value);
          truncate = displayVal?.length > MAX_CHARS_TO_RENDER && !isFullscreen && !isCellSelected;
        } else {
          displayVal = "—";
          truncate = false;
        }
      } else if (cell?.error_message) {
        displayVal = cell.error_message;
        truncate = false;
      } else {
        displayVal = "—";
        truncate = false;
      }
      break;
  }

  if (cell?.status === CellStatus.FAILED) {
    displayVal = cell.error_message || "—";
  }

  const copyValue = useCallback(
    (event: KeyboardEvent) => {
      if (isCellSelected && isModifierKeyPressed(event) && event.key.toLowerCase() === "c") {
        if (column?.column_type === ColumnType.COMPARE) {
          navigator.clipboard.writeText(cell?.display_value?.value);
        } else {
          navigator.clipboard.writeText(displayVal);
        }
      }
    },
    [displayVal, isCellSelected, column?.column_type, cell?.display_value?.value],
  );

  useEffect(() => {
    document.addEventListener("keydown", copyValue);

    return () => {
      document.removeEventListener("keydown", copyValue);
    };
  }, [copyValue]);

  return {
    displayValue: displayVal,
    shouldTruncate: truncate,
  };
};
